/**
 * React Hook: useTokenAutoRefresh
 *
 * Automatically refreshes streaming tokens before they expire
 * to ensure uninterrupted audio playback for long meditation sessions.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { refreshStreamingToken, AudioStreamingTokenResponse } from '@/utils/audioStreaming';

interface UseTokenAutoRefreshOptions {
  streamingUrl: string | null;
  isPlaying: boolean;
  enabled?: boolean;
}

interface UseTokenAutoRefreshResult {
  currentStreamingUrl: string | null;
  isRefreshing: boolean;
  refreshError: Error | null;
}

/**
 * Hook to automatically refresh streaming tokens during playback
 *
 * Refreshes the token 5 minutes before expiry to ensure seamless playback.
 * Only refreshes when audio is actively playing.
 *
 * @param options - Configuration options
 * @returns Object with current streaming URL, refresh state, and error
 *
 * @example
 * const { currentStreamingUrl } = useTokenAutoRefresh({
 *   streamingUrl: initialUrl,
 *   isPlaying: true,
 *   enabled: true
 * });
 */
export const useTokenAutoRefresh = ({
  streamingUrl,
  isPlaying,
  enabled = true
}: UseTokenAutoRefreshOptions): UseTokenAutoRefreshResult => {
  const [currentStreamingUrl, setCurrentStreamingUrl] = useState<string | null>(streamingUrl);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<Error | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tokenExpiresAtRef = useRef<number | null>(null);

  // Extract token from streaming URL
  const extractToken = useCallback((url: string | null): string | null => {
    if (!url) return null;

    const urlObj = new URL(url, window.location.origin);
    return urlObj.searchParams.get('token');
  }, []);

  // Update current URL when initial URL changes
  useEffect(() => {
    if (streamingUrl) {
      setCurrentStreamingUrl(streamingUrl);
      // Set expiry time (tokens expire in 1 hour = 3600 seconds)
      tokenExpiresAtRef.current = Date.now() + 3600 * 1000;
    }
  }, [streamingUrl]);

  // Auto-refresh logic
  useEffect(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Don't refresh if:
    // - Not enabled
    // - Not playing
    // - No streaming URL
    // - No expiry time set
    if (!enabled || !isPlaying || !currentStreamingUrl || !tokenExpiresAtRef.current) {
      return;
    }

    const token = extractToken(currentStreamingUrl);
    if (!token) {
      console.warn('⚠️ No token found in streaming URL, cannot refresh');
      return;
    }

    // Calculate time until refresh (5 minutes before expiry)
    const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
    const timeUntilRefresh = tokenExpiresAtRef.current - Date.now() - REFRESH_BUFFER_MS;

    // If already past refresh time, refresh immediately
    if (timeUntilRefresh <= 0) {
      console.log('🔄 Token refresh overdue, refreshing immediately');
      performRefresh(token);
      return;
    }

    // Schedule refresh
    console.log(`⏰ Scheduling token refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
    refreshTimerRef.current = setTimeout(() => {
      performRefresh(token);
    }, timeUntilRefresh);

    // Cleanup on unmount or dependency change
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [enabled, isPlaying, currentStreamingUrl, extractToken]);

  const performRefresh = async (token: string) => {
    setIsRefreshing(true);
    setRefreshError(null);

    try {
      console.log('🔄 Refreshing streaming token...');

      const response: AudioStreamingTokenResponse = await refreshStreamingToken(token);

      console.log('✅ Token refreshed successfully');
      setCurrentStreamingUrl(response.streaming_url);

      // Update expiry time
      tokenExpiresAtRef.current = Date.now() + response.expires_in * 1000;

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Token refresh failed');
      console.error('❌ Token refresh failed:', err);
      setRefreshError(err);

      // Don't update URL on error - keep using existing token until it expires
      // The audio player will naturally fail when token expires, prompting user to restart
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    currentStreamingUrl,
    isRefreshing,
    refreshError
  };
};
